import { Component, forwardRef, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCheckboxChange } from '@angular/material/checkbox';

export interface CheckboxOption<T> {
  label: string;
  value: T;
}

export interface CheckboxChangeEvent<T> {
  option: CheckboxOption<T>;
  checked: boolean;
}

@Component({
  selector: 'app-checkbox-group',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCheckboxModule],
  template: `
    <div class="checkbox-group">
      @for (option of options; track option.value) {
        <mat-checkbox
          color="primary"
          [checked]="isChecked(option.value)"
          (change)="onCheckboxChange(option, $event)"
        >
          {{ option.label }}
        </mat-checkbox>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxGroupComponent),
      multi: true,
    },
  ],
  styles: [
    `
      .checkbox-group__item {
        display: block;
        margin-bottom: 8px;
      }
    `
  ]
})
export class CheckboxGroupComponent<T> implements ControlValueAccessor {
  @Input() options: CheckboxOption<T>[] = [];
  @Output() checkboxChange = new EventEmitter<CheckboxChangeEvent<T>>();
  
  private selectedValues: T[] = [];
  private onChange: (value: T[]) => void = () => { /* default empty function */ };
  private onTouched: () => void = () => { /* default empty function */ };

  writeValue(value: T[]): void {
    if (value) {
      this.selectedValues = [...value]; // Create a copy to avoid reference issues
    } else {
      this.selectedValues = [];
    }
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Left empty as per requirements, but can be implemented if needed
  }

  isChecked(value: T): boolean {
    return this.selectedValues.includes(value);
  }

  onCheckboxChange(option: CheckboxOption<T>, event: MatCheckboxChange): void {
    console.log(`Checkbox changed: ${option.label}, checked: ${event.checked}`);
    const isChecked = event.checked;
    const value = option.value;
    
    if (isChecked) {
      if (!this.selectedValues.includes(value)) {
        this.selectedValues = [...this.selectedValues, value];
      }
    } else {
      this.selectedValues = this.selectedValues.filter(v => v !== value);
    }
    
    this.checkboxChange.emit({
      option: option,
      checked: isChecked
    });
    
    this.onChange([...this.selectedValues]); // Send a copy
    this.onTouched();
  }
}